import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Route guard that applies the nav's own check (design eval F27).
 *
 * The rail hides an entry when usePermissions().canAccessRoute(href) is false;
 * this renders a route only when the same call allows it, so a typed URL can't
 * reach what the nav withholds. `path` is the nav href the route belongs to:
 * the player and the join page pass "/sessions/…", which the /sessions prefix
 * in utils/permissions ADMIN_ONLY_ROUTES covers.
 *
 * It renders nothing until the profile has loaded, so an
 * admin is never bounced before their role is known.
 *
 * This is the console's guard only. The backend must enforce the same rules on
 * every endpoint these pages call.
 */
export default function RequireRoute({ path, children }: { path: string; children: ReactNode }) {
  const { userInfo, isLoading } = useUser();
  const { canAccessRoute } = usePermissions();

  if (!userInfo) return isLoading ? null : <Navigate to="/dashboard" replace />;
  if (!canAccessRoute(path)) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}
